import React, { useState, useEffect, useCallback } from "react";
import AppBar from "@mui/material/AppBar";
import Box from "@mui/material/Box";
import CssBaseline from "@mui/material/CssBaseline";
import Divider from "@mui/material/Divider";
import Drawer from "@mui/material/Drawer";
import IconButton from "@mui/material/IconButton";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import MenuIcon from "@mui/icons-material/Menu";
import Toolbar from "@mui/material/Toolbar";
import Typography from "@mui/material/Typography";
import { Link } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import HomeIcon from "@mui/icons-material/Home";
import DashboardIcon from "@mui/icons-material/Dashboard";
import CarIcon from "@mui/icons-material/DirectionsCar";
import { api } from "common";
import { colors } from "../components/Theme/WebTheme";
import { useTranslation } from "react-i18next";
import AccountBalanceWalletOutlinedIcon from "@mui/icons-material/AccountBalanceWalletOutlined";
import LogoutOutlinedIcon from "@mui/icons-material/LogoutOutlined";
import GppBadOutlinedIcon from "@mui/icons-material/GppBadOutlined";
import HeadsetMicIcon from "@mui/icons-material/HeadsetMic";
import AddLocationAltOutlinedIcon from "@mui/icons-material/AddLocationAltOutlined";
import FormatListBulletedIcon from "@mui/icons-material/FormatListBulleted";
import QueueIcon from "@mui/icons-material/Queue";
import EmojiPeopleIcon from "@mui/icons-material/EmojiPeople";
import SettingsOutlinedIcon from "@mui/icons-material/SettingsOutlined";
import MoneyIcon from "@mui/icons-material/Money";
import DiscountOutlinedIcon from "@mui/icons-material/DiscountOutlined";
import NotificationsActiveOutlinedIcon from "@mui/icons-material/NotificationsActiveOutlined";
import AssessmentOutlinedIcon from "@mui/icons-material/AssessmentOutlined";
import PermIdentityOutlinedIcon from "@mui/icons-material/PermIdentityOutlined";
import AnnouncementOutlinedIcon from "@mui/icons-material/AnnouncementOutlined";
import { Category, CommentBankRounded, CreditCard } from "@mui/icons-material";
import SubscriptionsIcon from "@mui/icons-material/Subscriptions";
import LoyaltyIcon from "@mui/icons-material/Loyalty";
import SecurityIcon from "@mui/icons-material/Security";
import PaymentIcon from "@mui/icons-material/Payment";
import PaymentOutlinedIcon from "@mui/icons-material/PaymentOutlined";
import DocumentScannerOutlined from "@mui/icons-material/DocumentScannerOutlined";
import RateReviewIcon from "@mui/icons-material/RateReview";
import RolesAPI from "../common/rolesApi";
import SmartToyIcon from "@mui/icons-material/SmartToy";
import MicIcon from "@mui/icons-material/Mic";
import BookIcon from "@mui/icons-material/Book";
import TuneIcon from "@mui/icons-material/Tune";
import LibraryBooksIcon from "@mui/icons-material/LibraryBooks";
import ExpandLess from "@mui/icons-material/ExpandLess";
import ExpandMore from "@mui/icons-material/ExpandMore";
import Collapse from "@mui/material/Collapse";
import { FirebaseConfig } from "config/FirebaseConfig";

const drawerWidth = 260;

export default function ResponsiveDrawer(props) {
    const { t, i18n } = useTranslation();
    const isRTL = i18n.dir();
    const { window } = props;
    const [mobileOpen, setMobileOpen] = React.useState(false);
    const { signOff } = api;
    const auth = useSelector((state) => state.auth);
    const settings = useSelector((state) => state.settingsdata.settings);
    const dispatch = useDispatch();
    const addonsdata = useSelector((state) => state.addonsdata);

    const LogOut = () => {
        dispatch(signOff());
    };

    const [role, setRole] = useState(null);
    const [userPermissions, setUserPermissions] = useState([]);
    const [powerTranzActive, setPowerTranzActive] = useState(false);
    const [aiMenuOpen, setAiMenuOpen] = useState(false);

    const isSubMenuAdminPermissionGranted = (permissionName) => {
        try {
            if (
                addonsdata.addons &&
                addonsdata.addons.sub_admin_controls === true
            ) {
                if (!auth.profile.subAdminPermissions) {
                    return true;
                }
                if (
                    auth.profile.subAdminPermissions.type == "all" ||
                    auth.profile.subAdminPermissions.type == ""
                ) {
                    return true;
                } else if (
                    auth.profile.subAdminPermissions.type == "specific"
                ) {
                    console.log(
                        "🔍 Specific permissions for:",
                        auth.profile.subAdminPermissions.pages[permissionName],
                        permissionName
                    );
                    return auth.profile.subAdminPermissions.pages[
                        permissionName
                    ];
                } else {
                    return false;
                }
            }
            return true;
        } catch (error) {
            return true;
        }
    };

    const fetchUserPermissions = useCallback(async () => {
        try {
            const response = await RolesAPI.getUserRole(auth.profile.uid);

            if (response.success && response.user) {
                console.log(
                    "🔍 User permissions fetched:",
                    response.user.permissions
                );
                setUserPermissions(response.user.permissions || []);
            } else {
                console.log("🔍 No custom permissions found, using default");
                setUserPermissions([]);
            }
        } catch (error) {
            console.error("Error fetching user permissions:", error);
            setUserPermissions([]);
        }
    }, [auth.profile.uid]);
    useEffect(() => {
        if (auth.profile && auth.profile.usertype) {
            setRole(auth.profile.usertype);

            // Fetch user permissions for custom roles
            if (auth.profile.uid) {
                fetchUserPermissions();
            }
        }
    }, [auth.profile, fetchUserPermissions]);

    // Check PowerTranz settings
    useEffect(() => {
        const checkPowerTranzSettings = async () => {
            try {
                // Use the existing payment settings endpoint
                const response = await fetch(
                    `https://us-central1-${FirebaseConfig.projectId}.cloudfunctions.net/get_providers`
                );
                if (response.ok) {
                    const data = await response.json();
                    // Check if PowerTranz is in the providers list
                    const hasPowerTranz =
                        data.providers &&
                        data.providers.some(
                            (provider) =>
                                provider.name === "powertranz" &&
                                provider.active
                        );
                    setPowerTranzActive(hasPowerTranz);
                }
            } catch (error) {
                console.error("Error checking PowerTranz settings:", error);
            }
        };

        if (auth.profile.usertype === "admin") {
            checkPowerTranzSettings();
        }
    }, [auth.profile.usertype]);

    // Check if admin queue addon is enabled
    const isAdminQueueAddonEnabled =
        settings?.admin_queue_addon_enabled === true;

    // Helper function to check if user has access to a menu item
    const hasMenuAccess = (menuItem) => {
        console.log(
            "🔍 Checking access for menu:",
            menuItem.name,
            "permission:",
            menuItem.permission
        );

        // Special case: essential menu items should always be available for all users
        const essentialMenus = ["logout", "profile", "home", "ai_menu"];
        if (essentialMenus.includes(menuItem.permission)) {
            console.log("🔍 Essential menu always available:", menuItem.name);
            return true;
        }

        // Special case: subscription menu should be hidden if subscriptions are disabled
        if (
            menuItem.permission === "subscriptions" &&
            settings &&
            settings.enable_subscriptions === false
        ) {
            console.log(
                "🔍 Subscription menu hidden - subscriptions disabled in settings"
            );
            return false;
        }

        // Special case: withdraws menu should be hidden if manual_withdraw_add_on is disabled
        if (
            menuItem.permission === "withdraws" &&
            settings &&
            settings.manual_withdraw_add_on !== true
        ) {
            console.log(
                "🔍 Withdraws menu hidden - manual_withdraw_add_on not enabled in settings"
            );
            return false;
        }

        // Special case: document management should be available for admin
        if (menuItem.permission === "document_management" && role === "admin") {
            console.log(
                "🔍 Document management available for admin:",
                menuItem.name
            );
            return true;
        }

        if (
            menuItem.permission === "complain" &&
            settings.support_module_enabled === true
        ) {
            console.log("🔍 Complain available for admin:", menuItem.name);
            return false;
        }

        if (
            menuItem.permission === "support_management" &&
            role === "admin" &&
            settings.support_module_enabled === true
        ) {
            console.log(
                "🔍 Support management available for admin:",
                menuItem.name
            );
            return true;
        }

        // Special case: bank details should be available for admin
        if (menuItem.permission === "bank_account" && role === "admin") {
            console.log("🔍 Bank details available for admin:", menuItem.name);
            return true;
        }

        // Special case: bank details should be available for admin
        if (
            menuItem.permission === "admin_queue" &&
            (role === "admin" || role === "fleetadmin")
        ) {
            return true;
        }

        // Debug for Bank Account specifically
        if (menuItem.permission === "bank_account") {
            console.log("🔍 Bank Account menu check:", {
                name: menuItem.name,
                permission: menuItem.permission,
                role: role,
                userPermissions: userPermissions,
                hasAccess: menuItem.access.includes(role),
            });
        }

        if (
            menuItem.permission === "feedback_review" &&
            addonsdata.addons.feedback_review_module_enabled === true
        ) {
            return true;
        }

        // Prioritize custom role over usertype (even for admin)
        if (userPermissions.length > 0) {
            console.log(
                "🔍 Using custom role permissions for menu:",
                menuItem.name,
                userPermissions
            );
            return userPermissions.includes(menuItem.permission);
        }

        // Only if no custom role, then check admin usertype
        if (role === "admin") {
            console.log(
                "🔍 Admin user without custom role, allowing menu access:",
                menuItem.name
            );
            return true;
        }

        // Fallback to default access control for other usertypes
        console.log(
            "🔍 Using default access control for menu:",
            menuItem.name,
            role
        );
        return menuItem?.access?.includes(role);
    };

    const handleDrawerToggle = () => {
        setMobileOpen(!mobileOpen);
    };

    const handleAiMenuToggle = () => {
        setAiMenuOpen(!aiMenuOpen);
    };

    const drawer = (
        <div style={{ backgroundColor: colors.MAIN_COLOR, height: "100%" }}>
            <div
                style={{
                    display: "flex",
                    backgroundColor: colors.MAIN_COLOR,
                    justifyContent: "center",
                }}
            >
                <img
                    style={{
                        marginTop: "20px",
                        marginBottom: "20px",
                        width: "190px",
                    }}
                    src={require("../assets/img/logotylo.png").default}
                    alt="Logo"
                />
            </div>
            <div style={{ backgroundColor: colors.MAIN_COLOR }}>
                {role ? (
                    <List disablePadding={true} dense={false}>
                        {[
                            {
                                name: t("home"),
                                url: "/",
                                icon: <HomeIcon />,
                                access: [
                                    "admin",
                                    "fleetadmin",
                                    "driver",
                                    "customer",
                                ],
                                permission: "home",
                            },
                            ...(isSubMenuAdminPermissionGranted("dashboard")
                                ? [
                                      {
                                          name: t("dashboard_text"),
                                          url: "/dashboard",
                                          icon: <DashboardIcon />,
                                          access: ["admin", "fleetadmin"],
                                          permission: "dashboard",
                                      },
                                  ]
                                : []),
                            ...(isSubMenuAdminPermissionGranted("bookings")
                                ? [
                                      {
                                          name: t("booking_history"),
                                          url: "/bookings",
                                          icon: <FormatListBulletedIcon />,
                                          access: [
                                              "admin",
                                              "fleetadmin",
                                              "driver",
                                              "customer",
                                          ],
                                          permission: "booking_history",
                                      },
                                  ]
                                : []),
                            // Admin Queue menu - only show when admin queue addon is enabled
                            ...(isAdminQueueAddonEnabled &&
                            isSubMenuAdminPermissionGranted("queuedOrders")
                                ? [
                                      {
                                          name: t("admin_queue"),
                                          url: "/queued-orders",
                                          icon: <QueueIcon />,
                                          access: ["admin", "fleetadmin"],
                                          permission: "admin_queue",
                                      },
                                  ]
                                : []),
                            ...(isSubMenuAdminPermissionGranted("addbookings")
                                ? [
                                      {
                                          name: t("addbookinglable"),
                                          url: "/addbookings",
                                          icon: <AddLocationAltOutlinedIcon />,
                                          access: [
                                              "admin",
                                              "fleetadmin",
                                              "customer",
                                          ],
                                          permission: "add_bookings",
                                      },
                                  ]
                                : []),

                            ...(isSubMenuAdminPermissionGranted("users")
                                ? [
                                      {
                                          name: t("user"),
                                          url: "/users",
                                          icon: <EmojiPeopleIcon />,
                                          access: ["admin", "fleetadmin"],
                                          permission: "users",
                                      },
                                  ]
                                : []),
                            ...(isSubMenuAdminPermissionGranted("pricing")
                                ? [
                                      {
                                          name: t("pricing"),
                                          url: "/pricing",
                                          icon: <Category />,
                                          access: ["admin"],
                                          permission: "pricing",
                                      },
                                  ]
                                : []),
                            ...(isSubMenuAdminPermissionGranted("bank_account")
                                ? [
                                      {
                                          name: t("bank_account"),
                                          url: "/bank-account",
                                          icon: <CreditCard />,
                                          access: ["admin"],
                                          permission: "bank_account",
                                      },
                                  ]
                                : []),
                            ...(isSubMenuAdminPermissionGranted("subscriptions")
                                ? [
                                      {
                                          name: t("subscription"),
                                          url: "/subscriptions",
                                          icon: <SubscriptionsIcon />,
                                          access: ["admin"],
                                          permission: "subscriptions",
                                      },
                                  ]
                                : []),
                            ...(isSubMenuAdminPermissionGranted(
                                "loyalty_points"
                            )
                                ? [
                                      {
                                          name: t("loyalty_points"),
                                          url: "/loyalty-points",
                                          icon: <LoyaltyIcon />,
                                          access: ["admin"],
                                          permission: "loyalty_points",
                                      },
                                  ]
                                : []),
                            ...(isSubMenuAdminPermissionGranted("cars")
                                ? [
                                      {
                                          name: t("cars"),
                                          url: "/cars",
                                          icon: <CarIcon />,
                                          access: [
                                              "admin",
                                              "fleetadmin",
                                              "driver",
                                          ],
                                          permission: "cars",
                                      },
                                  ]
                                : []),
                            ...(isSubMenuAdminPermissionGranted("addtowallet")
                                ? [
                                      {
                                          name: t("add_to_wallet"),
                                          url: "/addtowallet",
                                          icon: (
                                              <AccountBalanceWalletOutlinedIcon />
                                          ),
                                          access: ["admin"],
                                          permission: "add_to_wallet",
                                      },
                                  ]
                                : []),
                            ...(isSubMenuAdminPermissionGranted(
                                "yappy_wallet_topups"
                            )
                                ? [
                                      {
                                          name: t("yappy_wallet_topups"),
                                          url: "/yappy-wallet-topups",
                                          icon: <PaymentOutlinedIcon />,
                                          access: ["admin"],
                                          permission: "add_to_wallet",
                                      },
                                  ]
                                : []),
                            ...(isSubMenuAdminPermissionGranted("withdraws")
                                ? [
                                      {
                                          name: t("withdraws"),
                                          url: "/withdraws",
                                          icon: <MoneyIcon />,
                                          access: ["admin"],
                                          permission: "withdraws",
                                      },
                                  ]
                                : []),
                            ...(isSubMenuAdminPermissionGranted("promos")
                                ? [
                                      {
                                          name: t("promo"),
                                          url: "/promos",
                                          icon: <DiscountOutlinedIcon />,
                                          access: ["admin"],
                                          permission: "promos",
                                      },
                                  ]
                                : []),
                            // AI Menu - placed after promos
                            ...(role === "admin" || role === "fleetadmin"
                                ? [
                                      {
                                          name: "AI",
                                          url: "ai-menu",
                                          icon: <SmartToyIcon />,
                                          access: ["admin", "fleetadmin"],
                                          permission: "ai_menu",
                                          isAiMenu: true,
                                      },
                                  ]
                                : []),
                            ...(isSubMenuAdminPermissionGranted("notifications")
                                ? [
                                      {
                                          name: t("push_notification_title"),
                                          url: "/notifications",
                                          icon: (
                                              <NotificationsActiveOutlinedIcon />
                                          ),
                                          access: ["admin"],
                                          permission: "notifications",
                                      },
                                  ]
                                : []),
                            ...(isSubMenuAdminPermissionGranted("cancelreasons")
                                ? [
                                      {
                                          name: t("cancellation_reasons"),
                                          url: "/cancelreasons",
                                          icon: <GppBadOutlinedIcon />,
                                          access: ["admin"],
                                          permission: "cancellation_reasons",
                                      },
                                  ]
                                : []),
                            ...(isSubMenuAdminPermissionGranted("allreports")
                                ? [
                                      {
                                          name: t("report"),
                                          url: "/allreports",
                                          icon: <AssessmentOutlinedIcon />,
                                          access: ["admin", "fleetadmin"],
                                          permission: "reports",
                                      },
                                  ]
                                : []),
                            ...(isSubMenuAdminPermissionGranted("complain")
                                ? [
                                      {
                                          name: t("complain"),
                                          url: "/complain",
                                          icon: <AnnouncementOutlinedIcon />,
                                          access: ["admin"],
                                          permission: "complain",
                                      },
                                  ]
                                : []),
                            ...(isSubMenuAdminPermissionGranted("support")
                                ? [
                                      {
                                          name: t("support"),
                                          url: "/support",
                                          icon: <AnnouncementOutlinedIcon />,
                                          access: ["admin"],
                                          permission: "support_management",
                                      },
                                  ]
                                : []),
                            // ...(isSubMenuAdminPermissionGranted("roles")
                            //   ? [{
                            //     name: t("roles_management"),
                            //     url: "/roles",
                            //     icon: <SecurityIcon />,
                            //     access: ["admin"],
                            //     permission: "roles",
                            //   }]
                            //   : []),
                            // PowerTranz Transactions menu - only show if PowerTranz is active
                            ...(powerTranzActive &&
                            auth.profile.usertype === "admin" &&
                            isSubMenuAdminPermissionGranted(
                                "powertranz_transactions"
                            )
                                ? [
                                      {
                                          name: t("powertranz_transactions"),
                                          url: "/powertranz-transactions",
                                          icon: <PaymentIcon />,
                                          access: ["admin"],
                                          permission: "powertranz_transactions",
                                      },
                                  ]
                                : []),
                            // {
                            //   name: t("support_management") || "Support Management",
                            //   url: "/support",
                            //   icon: <HeadsetMicIcon />,
                            //   access: ["admin"],
                            //   permission: "support_management",
                            // },
                            ...(isSubMenuAdminPermissionGranted(
                                "documentmanagement"
                            )
                                ? [
                                      {
                                          name: t("document_management"),
                                          url: "/documentmanagement",
                                          icon: <DocumentScannerOutlined />,
                                          access: ["admin"],
                                          permission: "document_management",
                                      },
                                  ]
                                : []),
                            ...(isSubMenuAdminPermissionGranted(
                                "feedback_review"
                            )
                                ? [
                                      {
                                          name: t("feedback_review"),
                                          url: "/feedback-review",
                                          icon: <RateReviewIcon />,
                                          access: ["admin", "fleetadmin"],
                                          permission: "feedback_review",
                                      },
                                  ]
                                : []),
                            ...(isSubMenuAdminPermissionGranted("settings")
                                ? [
                                      {
                                          name: t("settings_title"),
                                          url: "/settings",
                                          icon: <SettingsOutlinedIcon />,
                                          access: ["admin"],
                                          permission: "settings",
                                      },
                                  ]
                                : []),
                            ...(isSubMenuAdminPermissionGranted("userwallet")
                                ? [
                                      {
                                          name: t("my_wallet_tile"),
                                          url: "/userwallet",
                                          icon: (
                                              <AccountBalanceWalletOutlinedIcon />
                                          ),
                                          access: [
                                              "driver",
                                              "customer",
                                              "fleetadmin",
                                          ],
                                          permission: "wallet",
                                      },
                                  ]
                                : []),
                            {
                                name: t("profile"),
                                url: "/profile",
                                icon: <PermIdentityOutlinedIcon />,
                                access: [
                                    "admin",
                                    "fleetadmin",
                                    "driver",
                                    "customer",
                                ],
                                permission: "profile",
                            },
                            {
                                name: t("logout"),
                                url: "logout",
                                icon: <LogoutOutlinedIcon />,
                                access: [
                                    "admin",
                                    "fleetadmin",
                                    "driver",
                                    "customer",
                                ],
                                permission: "logout",
                            },
                        ].map((item, index) =>
                            hasMenuAccess(item) ? (
                                <div key={"key" + index}>
                                    {item.isAiMenu ? (
                                        // AI Menu with submenu
                                        <>
                                            <div
                                                style={{
                                                    display: "flex",
                                                    height: "50px",
                                                }}
                                            >
                                                <ListItem
                                                    disableGutters={true}
                                                    disablePadding={true}
                                                    style={{ paddingLeft: 5 }}
                                                    alignItems="center"
                                                    dense={false}
                                                >
                                                    <ListItemButton
                                                        disableGutters={true}
                                                        dense={false}
                                                        onClick={
                                                            handleAiMenuToggle
                                                        }
                                                    >
                                                        <ListItemIcon
                                                            style={{
                                                                color: colors.WHITE,
                                                            }}
                                                        >
                                                            {item.icon}
                                                        </ListItemIcon>
                                                        <ListItemText
                                                            inset={false}
                                                            disableTypography={
                                                                false
                                                            }
                                                            primary={item.name}
                                                            style={{
                                                                color: colors.WHITE,
                                                                textAlign:
                                                                    isRTL ===
                                                                    "rtl"
                                                                        ? "right"
                                                                        : "left",
                                                            }}
                                                        />
                                                        {aiMenuOpen ? (
                                                            <ExpandLess
                                                                style={{
                                                                    color: colors.WHITE,
                                                                }}
                                                            />
                                                        ) : (
                                                            <ExpandMore
                                                                style={{
                                                                    color: colors.WHITE,
                                                                }}
                                                            />
                                                        )}
                                                    </ListItemButton>
                                                    <Divider />
                                                </ListItem>
                                            </div>

                                            <Collapse
                                                in={aiMenuOpen}
                                                timeout="auto"
                                                unmountOnExit
                                            >
                                                <List
                                                    component="div"
                                                    disablePadding
                                                >
                                                    {[
                                                        {
                                                            name: "AI Support",
                                                            url: "/ai-support",
                                                            icon: (
                                                                <HeadsetMicIcon />
                                                            ),
                                                        },
                                                        {
                                                            name: "AI Voice",
                                                            url: "/ai-voice",
                                                            icon: <MicIcon />,
                                                        },
                                                        {
                                                            name: "AI Booking",
                                                            url: "/ai-booking",
                                                            icon: <BookIcon />,
                                                        },
                                                        {
                                                            name: "AI Settings",
                                                            url: "/ai-settings",
                                                            icon: <TuneIcon />,
                                                        },
                                                        {
                                                            name: "Knowledgebase",
                                                            url: "/knowledgebase",
                                                            icon: (
                                                                <LibraryBooksIcon />
                                                            ),
                                                        },
                                                    ].map(
                                                        (subItem, subIndex) => (
                                                            <div
                                                                style={{
                                                                    display:
                                                                        "flex",
                                                                    height: "45px",
                                                                }}
                                                                key={
                                                                    "ai-sub-" +
                                                                    subIndex
                                                                }
                                                            >
                                                                <ListItem
                                                                    disableGutters={
                                                                        true
                                                                    }
                                                                    disablePadding={
                                                                        true
                                                                    }
                                                                    style={{
                                                                        paddingLeft: 25,
                                                                    }}
                                                                    alignItems="center"
                                                                    dense={
                                                                        false
                                                                    }
                                                                >
                                                                    <ListItemButton
                                                                        disableGutters={
                                                                            true
                                                                        }
                                                                        dense={
                                                                            false
                                                                        }
                                                                        component={
                                                                            Link
                                                                        }
                                                                        to={
                                                                            subItem.url
                                                                        }
                                                                        style={{
                                                                            paddingLeft: 20,
                                                                        }}
                                                                    >
                                                                        <ListItemIcon
                                                                            style={{
                                                                                color: colors.WHITE,
                                                                                minWidth: 35,
                                                                                fontSize:
                                                                                    "1.2rem",
                                                                            }}
                                                                        >
                                                                            {
                                                                                subItem.icon
                                                                            }
                                                                        </ListItemIcon>
                                                                        <ListItemText
                                                                            inset={
                                                                                false
                                                                            }
                                                                            disableTypography={
                                                                                false
                                                                            }
                                                                            primary={
                                                                                subItem.name
                                                                            }
                                                                            style={{
                                                                                color: colors.WHITE,
                                                                                textAlign:
                                                                                    isRTL ===
                                                                                    "rtl"
                                                                                        ? "right"
                                                                                        : "left",
                                                                                fontSize:
                                                                                    "0.9rem",
                                                                            }}
                                                                        />
                                                                    </ListItemButton>
                                                                    <Divider />
                                                                </ListItem>
                                                            </div>
                                                        )
                                                    )}
                                                </List>
                                            </Collapse>
                                        </>
                                    ) : (
                                        // Regular menu item
                                        <div
                                            style={{
                                                display: "flex",
                                                height: "50px",
                                            }}
                                        >
                                            <ListItem
                                                key={item}
                                                disableGutters={true}
                                                disablePadding={true}
                                                style={{ paddingLeft: 5 }}
                                                alignItems="center"
                                                dense={false}
                                            >
                                                <ListItemButton
                                                    disableGutters={true}
                                                    dense={false}
                                                    component={Link}
                                                    to={
                                                        item.url === "logout"
                                                            ? null
                                                            : item.url
                                                    }
                                                    onClick={
                                                        item.url === "logout"
                                                            ? LogOut
                                                            : null
                                                    }
                                                >
                                                    <ListItemIcon
                                                        style={{
                                                            color: colors.WHITE,
                                                        }}
                                                    >
                                                        {item.icon}
                                                    </ListItemIcon>
                                                    <ListItemText
                                                        inset={false}
                                                        disableTypography={
                                                            false
                                                        }
                                                        primary={item.name}
                                                        style={{
                                                            color: colors.WHITE,
                                                            textAlign:
                                                                isRTL === "rtl"
                                                                    ? "right"
                                                                    : "left",
                                                        }}
                                                    />
                                                </ListItemButton>
                                                <Divider />
                                            </ListItem>
                                        </div>
                                    )}
                                </div>
                            ) : null
                        )}
                    </List>
                ) : null}
            </div>
        </div>
    );

    const container =
        window !== undefined ? () => window().document.body : undefined;

    return (
        <Box
            sx={{ display: "flex", direction: isRTL === "rtl" ? "rtl" : "ltr" }}
        >
            <CssBaseline />
            <AppBar
                position="fixed"
                style={
                    isRTL === "rtl"
                        ? {
                              marginRight: drawerWidth,
                              backgroundColor: colors.MAIN_COLOR,
                          }
                        : {
                              marginLeft: drawerWidth,
                              backgroundColor: colors.MAIN_COLOR,
                          }
                }
                sx={{
                    width: { sm: `calc(100% - ${drawerWidth}px)` },
                    ml: { sm: `${drawerWidth}px` },
                }}
            >
                <Toolbar>
                    <IconButton
                        color={colors.WHITE}
                        aria-label="open drawer"
                        edge="start"
                        onClick={handleDrawerToggle}
                        sx={{ mr: 2, display: { sm: "none" } }}
                    >
                        <MenuIcon style={{ color: colors.WHITE }} />
                    </IconButton>
                    <Typography
                        variant="h6"
                        noWrap
                        component="div"
                        style={{ color: colors.WHITE }}
                    >
                        {settings && settings.appName ? settings.appName : ""}
                    </Typography>
                </Toolbar>
            </AppBar>
            <Box
                component="nav"
                sx={{ width: { sm: drawerWidth }, flexShrink: { sm: 0 } }}
                aria-label="mailbox folders"
            >
                <Drawer
                    container={container}
                    variant="temporary"
                    anchor={isRTL === "rtl" ? "right" : "left"}
                    open={mobileOpen}
                    onClose={handleDrawerToggle}
                    ModalProps={{
                        keepMounted: true,
                    }}
                    sx={{
                        display: { xs: "block", sm: "none" },
                        "& .MuiDrawer-paper": {
                            boxSizing: "border-box",
                            width: drawerWidth,
                        },
                        direction: isRTL === "rtl" ? "rtl" : "ltr",
                    }}
                >
                    {drawer}
                </Drawer>
                <Drawer
                    variant="permanent"
                    anchor={isRTL === "rtl" ? "right" : "left"}
                    sx={{
                        display: { xs: "none", sm: "block" },
                        "& .MuiDrawer-paper": {
                            boxSizing: "border-box",
                            width: drawerWidth,
                        },
                        direction: isRTL === "rtl" ? "rtl" : "ltr",
                    }}
                    open
                >
                    {drawer}
                </Drawer>
            </Box>
            <Box
                component="main"
                sx={{
                    flexGrow: 1,
                    p: 1,
                    width: { sm: `calc(100% - ${drawerWidth}px)` },
                }}
            >
                <Toolbar />
                {props.children}
            </Box>
        </Box>
    );
}
